import * as Sentry from "@sentry/vue";
import { useClaimStore, useContextStore, useNotificationStore } from "@/store";
import { auth0 } from "@/services/auth0";

import Axios, { AxiosInstance, AxiosRequestConfig } from "axios";

export function setupAxiosInterceptors(axios: AxiosInstance) : void {
  axios.interceptors.request.use(requestInterceptor);
  axios.interceptors.response.use(fulfilledInterceptor, rejectedInterceptor);
}

async function requestInterceptor(req: AxiosRequestConfig) {
  const contextStore = useContextStore();
  const idToken = auth0.idTokenClaims.value;
  const accessToken = await auth0.getAccessTokenSilently();
  req.headers ??= {}
  req.headers['Authorization'] = `Bearer ${accessToken}`;
  req.headers['front-user-id'] = contextStore.user.frontId;
  req.headers['front-user-email'] = contextStore.user.email;
  req.headers['front-user-fullname'] = contextStore.user.fullName;
  req.withCredentials = true;
  const isCancelled = await isRequestCancelled(req);

  if (isCancelled) throw new Axios.Cancel('Operation canceled by the user.')

  return req as any;
}

async function isRequestCancelled(req: AxiosRequestConfig) : Promise<boolean> {
  const method = req.method?.toUpperCase();

  const isEditorJsSave = req.data?.blocks; // Quick & Dirty
  if (method && method === 'DELETE' && !isEditorJsSave) {
    const notif = useNotificationStore().emitNotification({
      title: "Envoie de la requête",
      // message: "Impossible d'extraitre les passagers",
      type: 'cancellableRequest'
    }, 3000);

    return new Promise(resolve => {
      notif.eventBus.on((eventName) => {
        if (eventName === 'cancel-request') return resolve(true)
        else if (eventName === 'close') return resolve(false)
      });
    })
  }

  return false;
}

function fulfilledInterceptor(req: AxiosRequestConfig) {
  const method = req.method?.toUpperCase();
  if (method && method !== 'OPTIONS' && method !== 'GET') {
    useNotificationStore().emitNotification({
      title: "Opération réussie",
      type: "success"
    })
  }

  const transaction = Sentry.startTransaction({
    name: `${req.method?.toUpperCase()} ${req.baseURL}`,
    op: "request"
  });

  Sentry.configureScope((scope) => {
    scope.setSpan(transaction);

    scope.addBreadcrumb({
      type: "http",
      category: "xhr",
      message: "Axios",
      data: {
        url: req.url,
        method: req.method?.toUpperCase(),
        params: req.params,
        body: req.data
      }
    })
  });

  return req as any;
}

function rejectedInterceptor(err: any) {
  const expectedErrors = err instanceof Axios.Cancel
  
  if (err.response?.status === 401 && !err.config?.__isRetryRequest) {
    useNotificationStore().emitNotification({
      title: "Problème d'authentification",
      message: "Essayez de relancer la fenêtre. Si le problème persiste veuillez nous contacter.",
      type: "warning"
    }, 10000);
  } else if (!expectedErrors) { // Send error to Sentry
    const errorData = err?.response?.data
    console.log("AxiosInterceptor:", errorData);
    useNotificationStore().emitNotification({
      title: errorData.message || "Une erreur est survenue",
      // message: errorData.message ? "L'api nous a retournée l'erreur suivante: " + err : "",
      type: "error"
    }, 7000);

    Sentry.configureScope((scope) => {
      scope.addBreadcrumb({
        type: "http",
        category: "xhr",
        message: "Response",
        data: errorData
      })
    })

    Sentry.captureException(err, {
      contexts: {
        claim: {
          id: useClaimStore().claim?.id,
          flightIdentifier: useClaimStore().claim?.legalSpecification.effectiveFlight.flightIdentifier
        },
        front: {
          conversationId: useContextStore().frontContext?.conversation?.id,
        }
      },
      tags: { axios: true },
    });
  }

  throw err;
}
